/*
 * Confidential and Proprietary.
 * Do not distribute without Bloom Harbor Gifts consent.
 * Copyright Bloom Harbor Gifts 2019. All rights reserved.
 */

import React from 'react';
import {
    object, string, arrayOf, array, shape, number,
} from 'prop-types';
import { useUIDSeed } from 'react-uid';
import LazyLoad from 'react-lazyload';
import Grid from '@material-ui/core/Grid';
import { Paper } from '@material-ui/core';
import { Star, StarHalf } from '@material-ui/icons';
import { useDispatch, useSelector } from 'react-redux';
import Slider from 'react-slick';
import pmallHelper from '../../../../../../helpers/pmall';
import { getActiveABTest } from '../../../../../../../state/ducks/App/ducks/ABTesting/ABTesting-Selectors';
import { getSSRDeviceType } from '../../../../../../../state/ducks/App/App-Selectors';
import useIsPlaPage from '../../../../../../helpers/hooks/useIsPlaPage';
import useIsDisplayChannel from '../../../../../../helpers/hooks/useIsDisplayChannel';
import { emitCustomTrackingEvent } from '../../../../../../../state/ducks/TagManager/ducks/ClickStreamEvents/ClickStreamEvent-Actions';

const ProductPlaImageUIMobile = ({
    classes,
    name,
    path,
    additionalImages,
    productName,
    deliveryMessage,
    productSkus,
    pmallSummaryInfo,
    presentationFamily,
}) => {
    const seed = useUIDSeed();
    const dispatch = useDispatch();
    const ssrDeviceType = useSelector(getSSRDeviceType);
    const isFood = (presentationFamily === 'food');
    const slidesToShow = isFood ? 1 : 1.5;

    /*
    * @description price array comparison
    * @param {arr} prices as numbers
    * @param (arr) prices as numbers
    * @return {bool} checks if same length and elements
    */
    const compareRetailAndSalePrices = (a, b) => a.length === b.length && a.every((elem, idx) => elem === b[idx]);

    const floralPLARedesign = useSelector(getActiveABTest('is_floral_pla_redesign'));

    const showPriceRanges = () => {
        const retailArr = [];
        const saleArr = [];
        if (productSkus) {
            productSkus.forEach((sku) => {
                retailArr.push(sku.prices[0]?.value);
                saleArr.push(sku.prices[1]?.value);
            });
            retailArr.sort();
            saleArr.sort();
        }

        if (compareRetailAndSalePrices(retailArr, saleArr)) {
            return (
                <div className={classes.sale}>
                    ${retailArr?.[0]} - ${retailArr?.[retailArr.length - 1]}
                </div>
            );
        }
        return (
            <>
                <div className={classes.retail}>
                    ${retailArr?.[0]} - ${retailArr?.[retailArr.length - 1]}
                </div>

                <div className={classes.sale}>
                    ${saleArr?.[0]} - ${saleArr?.[saleArr.length - 1]}
                </div>
            </>
        );
    };

    let additionalImageData = additionalImages;
    let primaryImageName = productName;
    let primaryImageSrc = `${path}${name}x.jpg`;

    const hasPmallData = pmallSummaryInfo?.customizations?.refnum && pmallSummaryInfo?.customizations?.refnum !== '';

    /**
     * if pmall data is present show the preview first and what was
     * supposed to be first as the second (first additional).
     *
     * The image urls are built on the fly using path and name
     * props.
     */
    if (hasPmallData) {
        // make the old primary the first additional
        additionalImageData = [name, ...additionalImageData];

        // set the primary image to be the pmall preview
        primaryImageName = pmallSummaryInfo.customizations.imageURL;
        primaryImageSrc = pmallHelper.buildURL(pmallSummaryInfo, true);
    }
    const isDisplayChannel = useIsDisplayChannel();
    const isPlaPage = useIsPlaPage() || isDisplayChannel;
    let PLAVariantStyle = false;
    if (isPlaPage && floralPLARedesign === 'VARIANT' && ssrDeviceType !== 'mobile') {
        PLAVariantStyle = true;
    } else if (isPlaPage && ssrDeviceType === 'mobile') {
        PLAVariantStyle = true;
    }

    const imageArray = [
        <img
            className={classes.plaSlide}
            alt={primaryImageName}
            src={primaryImageSrc}
            key={seed(primaryImageSrc)}
        />,
    ];

    additionalImageData.forEach((img) => {
        imageArray.push(
            <img
                key={seed(img)}
                className={classes.plaSlide}
                alt={productName}
                src={`${path}${img}x.jpg`}
            />,
        );
    });

    return (

        <>
            {PLAVariantStyle  ? (
                <>
                    <div className={classes.PLAImageContainer}>
                        <Slider
                            dots={isFood}
                            speed={500}
                            arrows={false}
                            infinite
                            slidesToShow={imageArray.length > 1 ? slidesToShow : 1}
                            slidesToScroll={1}
                            className={`carousel-container ${classes.carouselContainer} ${imageArray.length > 1 ? classes.carouselContainerPadding : ''}`}
                            dotsClass="slick-dots slick-thumb"
                            customPaging={() => <div className={classes.dotNavigation} />}
                            onSwipe={(direction) => {
                                dispatch(emitCustomTrackingEvent({
                                    eventType: 'custom',
                                    eventSubType: 'feature-tracking',
                                    action: 'Click',
                                    category: 'Product Image',
                                    label: 'Swipe on Image',
                                    tag: direction === 'left' ? 'Swipe Left' : 'Swipe Right',
                                }));
                            }}
                        >
                            {imageArray}
                        </Slider>
                    </div>
                </>
            ) : (
                <div className={classes.container}>
                    <Paper className={classes.img}>
                        <div className={classes.sliderContainer}>
                            <img className={classes.plaSlide} alt={primaryImageName} src={primaryImageSrc} />
                            {additionalImageData.map((img) => (
                                <LazyLoad key={seed(img)}>
                                    <img className={classes.plaSlide} alt={productName} src={`${path}${img}x.jpg`} />
                                </LazyLoad>
                            ))}
                        </div>
                    </Paper>
                    <Grid className={classes.plaSummaryContainer}>
                        <h1 className={classes.plaProdName}>{productName}</h1>
                        <div className={classes.reviewCountContainer}>
                            <div className={classes.ratingStars}>
                                <Star />
                                <Star />
                                <Star />
                                <Star />
                                <StarHalf />
                            </div>
                            <span className={classes.reviewCount}>(254,268 reviews)</span>
                        </div>
                        {showPriceRanges(productSkus)}
                        <div className={classes.plaDelivery}>{deliveryMessage}</div>
                    </Grid>
                </div>
            )}
        </>
    );
};

ProductPlaImageUIMobile.propTypes = {
    classes: object.isRequired,
    name: string.isRequired,
    path: string.isRequired,
    additionalImages: array.isRequired,
    productName: string.isRequired,
    deliveryMessage: string.isRequired,
    productSkus: arrayOf(shape({
        prices: arrayOf(shape({
            value: number.isRequired,
        })),
    })).isRequired,
    pmallSummaryInfo: shape({
        customizations: shape({
            refnum: string,
            imageURL: string,
            mobileURL: string,
        }),
    }),
    presentationFamily: string,
};

ProductPlaImageUIMobile.defaultProps = {
    presentationFamily: '',
    pmallSummaryInfo: {
        customizations: {
            count: 0,
            refnum: '',
            mobileURL: '',
            imageURL: '',
        },
    },
};

export default ProductPlaImageUIMobile;
